export const getChatServerUrl = () => {
  const apiKey = import.meta.env.VITE_CHAT_URL;
  return `${apiKey}/maia`;
};

export const getWorkflowServerUrl = () => {
  const apiKey = import.meta.env.VITE_WORKFLOWS_URL;
  return apiKey;
};

export const getUsageReportingUrl = () => {
  const apiEndpointUrl = import.meta.env.VITE_USAGE_REPORTING_URL;
  return apiEndpointUrl;
};

export function truncateText(text: string, length = 50) {
  if (text.length > length) {
    return text.substring(0, length) + " ...";
  }
  return text;
}

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const timeAgo = (dateString: string): string => {
  // Parse the date string into a Date object
  const timestamp = new Date(dateString);

  // Check for invalid date
  if (isNaN(timestamp.getTime())) {
    throw new Error("Invalid date string provided.");
  }

  // Get the current time
  const now = new Date();

  // Calculate the difference in milliseconds
  const timeDifference = now.getTime() - timestamp.getTime();

  // Convert time difference to minutes and hours
  const minutesAgo = Math.floor(timeDifference / (1000 * 60));
  const hoursAgo = Math.floor(minutesAgo / 60);

  // Format the date into a readable format e.g. "November 27"
  const options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric" };
  const formattedDate = timestamp.toLocaleDateString(undefined, options);

  // Determine the time difference string
  let timeAgoStr: string;
  if (minutesAgo < 1) {
    timeAgoStr = "just now";
  } else if (minutesAgo < 60) {
    // Less than an hour ago, display minutes
    timeAgoStr = `${minutesAgo} ${minutesAgo === 1 ? "minute" : "minutes"} ago`;
  } else if (hoursAgo < 24) {
    // Less than a day ago, display hours
    timeAgoStr = `${hoursAgo} ${hoursAgo === 1 ? "hour" : "hours"} ago`;
  } else {
    // More than a day ago, display the formatted date
    timeAgoStr = formattedDate;
  }

  // Return the final readable string
  return timeAgoStr;
};
export const generateFileName = (module: string = "MAIA Conversation") => {
  const now = new Date();

  // Get components of the date and time
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  // Format the timestamp
  const timestamp = `${year}-${month}-${day} ${hours}-${minutes}-${seconds}`;

  return `${timestamp} ${module}.txt`;
};

export const objectURLToBlob = async (objectURL: string): Promise<Blob> => {
  const response = await fetch(objectURL);
  const blob = await response.blob();
  return blob;
};

export const convertBase64 = (file: File | Blob) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const base64ToBlob = (base64String: string) => {
  const parts = base64String.split(";base64,");
  const contentType = parts[0].split(":")[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uint8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uint8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uint8Array], { type: contentType });
};

export const downloadBase64File = (fileName: string, base64String: string | undefined) => {
  if (!base64String) return;
  const blob = base64ToBlob(base64String);
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
};

export const downloadZipFromUrl = (url: string) => {
  /**
   * TODO: This function can be refactored and the common part with downloadBase64File can be put into a new function.
   */
  fetch(url)
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const filename = 'output.zip';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    })
    .catch(error => console.error('Error downloading file:', error));
};

//dictionary for mime-types
//accpets filetype and returns corresponding mime-types
export const fileTypeDictionary: { [key: string]: string } = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain"
};

export const allowedInSessionFileTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  "text/csv",
  'application/pdf',
  "application/msword",
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  "text/vtt"
]

export const allowedDocumentTranslationFileTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  "application/msword",
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/pdf'
]

export const allowedPresentationFileTypes = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf'
]

export const allowedVOPowerpointFileTypes = [
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
]

export const documentFileTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/vtt'
]

export const tabularDataFileTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv'
]

export const imageFileTypes = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif'
]

export const allowedNonOcrFileTypes = [
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "message/rfc822",
  "text/html",
  "application/vnd.ms-outlook",
  "application/pdf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/tab-separated-values",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/vtt"
]

export const allowedKbOcrFileTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif'
]

export const LlamaModels = ["Llama 3 70B Instruct", "Llama 3.2 90B Vision Instruct"]

const mapMimeTypeToExtension = (type: string): string => {
  if (type.startsWith('application/')) {
    const extension = type.split('/')[1];
    switch (extension) {
      case 'msword':
        return '.doc';
      case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
        return '.docx';
      case 'vnd.ms-excel':
        return '.xls';
      case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return '.xlsx';
      case 'pdf':
        return '.pdf';
      case 'vnd.ms-powerpoint':
        return '.ppt';
      case 'vnd.openxmlformats-officedocument.presentationml.presentation':
        return '.pptx';
      case 'vnd.ms-outlook':
        return '.msg';
      default:
        return '';
    }
  } else if (type.startsWith('image/')) {
    return `.${type.split('/')[1]}`;
  } else if (type.startsWith('text/')) {
    switch (type) {
      case 'text/plain':
        return '.txt';
      case 'text/html':
        return '.html';
      case 'text/tab-separated-values':
        return '.tsv';
      case 'text/csv':
        return '.csv';
      case 'text/vtt':
        return '.vtt';
      default:
        return '';
    }
  } else if (type === 'message/rfc822') {
    return '.eml';
  }
  return '';
};

export const getAcceptedFileFormatsArray = (types: string[]): string[] => {
  return types.map(mapMimeTypeToExtension).filter(Boolean);
};

export const getAcceptedFileFormats = (types: string[]) => {
  return getAcceptedFileFormatsArray(types).join(',');
};

export const getFileExtension = (filename: string) => {
  const lastDotIndex = filename.lastIndexOf(".");
  const fileExtension = lastDotIndex !== -1 ? filename.substring(lastDotIndex + 1) : ""
  return fileExtension
}

export const getMimeType = (word: string): string | null => {
  return fileTypeDictionary[word] || null;
};

export const readFileContent = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

/**
 * Formats a date string to local timezone, handling both UTC and non-UTC formats
 * @param dateString - ISO date string (UTC or local)
 * @param options - Intl.DateTimeFormat options
 * @param locale - Optional locale (defaults to browser locale)
 * @returns Formatted date string in local timezone
 */
export const formatDateToLocal = (
  dateString: string,
  options?: Intl.DateTimeFormatOptions,
  locale: string = navigator.language
): string => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return dateString;
    }

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    };

    return new Intl.DateTimeFormat(locale, options || defaultOptions).format(date);
  } catch (error) {
    console.warn("Error formatting date:", dateString, error);
    return dateString;
  }
};

export const handleDownload = (url: string | undefined, fileName: string | undefined) => {
  const link = document.createElement("a");
  if (url && fileName) {
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};